import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// A single tilted photovoltaic panel on two short steel posts: a dark-blue cell
// field in a light frame, angled up to catch the sun, with grid seam lines. The
// panel is built as one coplanar unit on a plane tilted about X, so the frame,
// cells and seams stay flush instead of floating apart.
const CELL = "#244a8c";
const GRID = "#3a64a8";
const FRAME = "#c2c6cd";
const POST = "#6b7079";

// One PV panel, centred at (cx,cy,cz), `w` wide (X) by `len` long up the slope,
// tilted up by `tilt` radians about X so the face points up and toward +Z.
function pvPanel(cx: number, cy: number, cz: number, w: number, len: number, tilt: number): Part[] {
  const c = Math.cos(tilt), s = Math.sin(tilt);
  // Map a local point (lx, ly, lz) on the panel into world space.
  const P = (lx: number, ly: number, lz: number): Vec3 => [cx + lx, cy + ly * c - lz * s, cz + ly * s + lz * c];
  const rot: Vec3 = [tilt, 0, 0];
  const parts: Part[] = [
    { geometry: new THREE.BoxGeometry(w, 0.03, len), color: FRAME, position: P(0, 0, 0), rotation: rot },
    { geometry: new THREE.BoxGeometry(w - 0.05, 0.024, len - 0.05), color: CELL, position: P(0, 0.018, 0), rotation: rot },
  ];
  // Two cross seams and one centre seam, raised just above the cell face.
  for (const lz of [-len / 4, 0, len / 4]) {
    parts.push({ geometry: new THREE.BoxGeometry(w - 0.05, 0.028, 0.012), color: GRID, position: P(0, 0.03, lz), rotation: rot });
  }
  parts.push({ geometry: new THREE.BoxGeometry(0.012, 0.028, len - 0.05), color: GRID, position: P(0, 0.03, 0), rotation: rot });
  return parts;
}

export const solarPanel = defineModel({
  id: "solar-panel",
  batchId: "electric",
  name: "Solar panel",
  tags: ["prop", "electric", "power", "energy", "solar", "renewable"],
  summary: "A tilted photovoltaic panel on two steel posts.",
  footprint: 0.4,
  parts(): Part[] {
    const tilt = 0.62; // face up and toward +Z (front)
    return [
      // Feet and posts: the back posts are taller so the panel leans up-forward.
      { geometry: new THREE.BoxGeometry(0.06, 0.05, 0.06), color: "#4a4a4e", position: [-0.24, 0.025, -0.16] },
      { geometry: new THREE.BoxGeometry(0.06, 0.05, 0.06), color: "#4a4a4e", position: [0.24, 0.025, -0.16] },
      { geometry: new THREE.CylinderGeometry(0.022, 0.022, 0.34, 8), color: POST, position: [-0.24, 0.2, -0.13] },
      { geometry: new THREE.CylinderGeometry(0.022, 0.022, 0.34, 8), color: POST, position: [0.24, 0.2, -0.13] },
      { geometry: new THREE.CylinderGeometry(0.022, 0.022, 0.18, 8), color: POST, position: [-0.24, 0.12, 0.14] },
      { geometry: new THREE.CylinderGeometry(0.022, 0.022, 0.18, 8), color: POST, position: [0.24, 0.12, 0.14] },
      ...pvPanel(0, 0.34, 0.0, 0.62, 0.52, tilt),
    ];
  },
  animations: [],
});
